import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { forgotPassword, resetPassword } from '../services/api';
import './ForgotPassword.css';

const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [identifier, setIdentifier] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();

    setError('');
    setSuccess('');

    const normalizedIdentifier = identifier.trim().toLowerCase();

    if (!normalizedIdentifier) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      const response = await forgotPassword(normalizedIdentifier);

      console.log('🔐 Forgot Password Response:', response.data);

      if (response.data?.success) {
        setSuccess(
          'A 6-digit verification code has been sent to your email address.'
        );

        setStep('reset');
      } else {
        setError(
          response.data?.error || 'Unable to send verification code'
        );
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Unable to send verification code'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    setError('');
    setSuccess('');

    const normalizedIdentifier = identifier.trim().toLowerCase();
    const normalizedCode = verificationCode.trim();

    if (!/^\d{6}$/.test(normalizedCode)) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword(
        normalizedIdentifier,
        normalizedCode,
        newPassword
      );

      if (response.data?.success) {
        setSuccess(
          'Your password has been reset successfully. Redirecting to login...'
        );

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      } else {
        setError(
          response.data?.error || 'Unable to reset password'
        );
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Unable to reset password'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        {step === 'request' ? (
          <>
            <h2>Reset Password</h2>

            <p>
              Enter your email address to receive a 6-digit
              verification code.
            </p>

            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            {success && (
              <div className="auth-success">
                {success}
              </div>
            )}

            <form onSubmit={handleRequestReset}>
              <div className="form-group">
                <input
                  type="email"
                  value={identifier}
                  onChange={(e) =>
                    setIdentifier(e.target.value)
                  }
                  required
                  placeholder="Email Address"
                  autoComplete="email"
                  className="reset-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="reset-button"
              >
                {loading
                  ? 'Sending...'
                  : 'Send Verification Code'}
              </button>
            </form>

            <div className="forgot-password-links">
              <Link to="/login">
                Back to Login
              </Link>
            </div>
          </>
        ) : (
          <>
            <h2>Verify Your Code</h2>

            <p>
              Enter the 6-digit verification code sent to
              your email address.
            </p>

            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            {success && (
              <div className="auth-success">
                {success}
              </div>
            )}

            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(
                      e.target.value.replace(/\D/g, '')
                    )
                  }
                  required
                  placeholder="6-Digit Verification Code"
                  autoComplete="one-time-code"
                  className="reset-input"
                />

                <small className="input-hint">
                  The verification code expires in 10 minutes.
                </small>
              </div>

              <div className="form-group">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) =>
                    setNewPassword(e.target.value)
                  }
                  required
                  minLength={6}
                  placeholder="New Password"
                  autoComplete="new-password"
                  className="reset-input"
                />
              </div>

              <div className="form-group">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) =>
                    setConfirmPassword(e.target.value)
                  }
                  required
                  minLength={6}
                  placeholder="Confirm New Password"
                  autoComplete="new-password"
                  className="reset-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="reset-button"
              >
                {loading
                  ? 'Resetting...'
                  : 'Reset Password'}
              </button>
            </form>

            <div className="forgot-password-links">
              <button
                type="button"
                className="forgot-password-back-button"
                onClick={() => {
                  setStep('request');
                  setVerificationCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setError('');
                  setSuccess('');
                }}
              >
                Request a New Code
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
